import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";

const emptyForm = { name: "", phone: "", email: "", subject: "", message: "" };

function ContactUs() {
  const [settings, setSettings] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, contactRes] = await Promise.all([
        axiosInstance.get("/api/settings"),
        axiosInstance.get("/api/contact"),
      ]);
      setSettings(settingsRes.data);
      setContact(contactRes.data);
    } catch (err) {
      console.error("Failed to load contact page data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);
    setErrorMsg("");

    try {
      // No auth needed — this is a genuinely public endpoint.
      // axiosInstance still works fine here since it only ATTACHES
      // a token if one exists; Flask's route doesn't require one.
      await axiosInstance.post("/api/enquiries", form);
      setSubmitStatus("success");
      setForm(emptyForm);
    } catch (err) {
      setSubmitStatus("error");
      setErrorMsg(
        err.response?.data?.error || "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const primaryColor = settings?.theme_primary_color || "#0d6efd";

  return (
    <div>
      <section
        className="py-4 text-white"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="container">
          <h1 className="fw-bold mb-0">Contact Us</h1>
          <p className="mb-0 opacity-75">We'd love to hear from you</p>
        </div>
      </section>

      <div className="container py-5">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : (
          <div className="row g-4">
            {/* Contact details + map */}
            <div className="col-12 col-lg-5">
              <h4 className="fw-bold mb-3">Get in Touch</h4>

              {contact?.address && (
                <div className="d-flex gap-3 mb-3">
                  <i
                    className="bi bi-geo-alt fs-5"
                    style={{ color: primaryColor }}
                  ></i>
                  <div>{contact.address}</div>
                </div>
              )}

              {contact?.phone_primary && (
                <div className="d-flex gap-3 mb-3">
                  <i
                    className="bi bi-telephone fs-5"
                    style={{ color: primaryColor }}
                  ></i>
                  <div>
                    <a
                      href={`tel:${contact.phone_primary}`}
                      className="text-decoration-none text-dark"
                    >
                      {contact.phone_primary}
                    </a>
                    {contact.phone_secondary && (
                      <>
                        {" / "}
                        <a
                          href={`tel:${contact.phone_secondary}`}
                          className="text-decoration-none text-dark"
                        >
                          {contact.phone_secondary}
                        </a>
                      </>
                    )}
                  </div>
                </div>
              )}

              {contact?.email && (
                <div className="d-flex gap-3 mb-3">
                  <i
                    className="bi bi-envelope fs-5"
                    style={{ color: primaryColor }}
                  ></i>
                  <a
                    href={`mailto:${contact.email}`}
                    className="text-decoration-none text-dark"
                  >
                    {contact.email}
                  </a>
                </div>
              )}

              {contact?.map_embed_url && (
                <div className="ratio ratio-4x3 mt-4 rounded overflow-hidden shadow-sm">
                  <iframe
                    src={contact.map_embed_url}
                    title="Location map"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>
              )}

              {(contact?.facebook_url ||
                contact?.instagram_url ||
                contact?.youtube_url ||
                contact?.twitter_url) && (
                <div className="d-flex gap-3 mt-4">
                  {contact.facebook_url && (
                    <a
                      href={contact.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: primaryColor }}
                    >
                      <i className="bi bi-facebook fs-4"></i>
                    </a>
                  )}
                  {contact.instagram_url && (
                    <a
                      href={contact.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: primaryColor }}
                    >
                      <i className="bi bi-instagram fs-4"></i>
                    </a>
                  )}
                  {contact.youtube_url && (
                    <a
                      href={contact.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: primaryColor }}
                    >
                      <i className="bi bi-youtube fs-4"></i>
                    </a>
                  )}
                  {contact.twitter_url && (
                    <a
                      href={contact.twitter_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: primaryColor }}
                    >
                      <i className="bi bi-twitter-x fs-4"></i>
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Enquiry form */}
            <div className="col-12 col-lg-7">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <h4 className="fw-bold mb-3">Send an Enquiry</h4>

                  {submitStatus === "success" && (
                    <div className="alert alert-success" role="alert">
                      <i className="bi bi-check-circle me-2"></i>
                      Thank you! Your enquiry has been submitted — we'll get
                      back to you soon.
                    </div>
                  )}

                  {submitStatus === "error" && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-circle me-2"></i>
                      {errorMsg}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Phone</label>
                        <input
                          type="text"
                          className="form-control"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Subject</label>
                        <input
                          type="text"
                          className="form-control"
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-12">
                        <label className="form-label">Message *</label>
                        <textarea
                          className="form-control"
                          name="message"
                          rows="4"
                          value={form.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                      </div>

                      <div className="col-12">
                        <small className="text-muted">
                          Please provide either an email or phone number so we
                          can reach you.
                        </small>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="btn mt-4 px-4 text-white"
                      style={{ backgroundColor: primaryColor }}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Enquiry <i className="bi bi-send ms-1"></i>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ContactUs;
